import ErrorIcon from '@mui/icons-material/Error';
import { FormControl, FormHelperText, TextField, useTheme, Button } from '@mui/material';
import { useState, type FC, type FormEvent, type PropsWithChildren } from 'react';
import { z } from 'zod';

import { notificationStrings } from '../../../../constants/strings';
import { UI_BUTTON_PRIMARY, UI_BUTTON_SECONDARY } from '../../../../constants/uiConstants';
import { getSamlQuery } from '../../../../queries/sessionQuery';
import { queryClient } from '../../../../router/Root';
import { useZustand } from '../../../../zustandStore';
import SimpleButton from '../../../atoms/button/simpleButton';
import { StyledBox } from '../common/styledBox';
import { StyledText } from '../common/styledText';

import { useEmailInput } from './hooks/useEmailInput';

const getWithQueryParam = (url: string, queryParamName: string, queryParamValue: string): string => {
	const urlObj = new URL(url);
	urlObj.searchParams.append(queryParamName, queryParamValue);
	return urlObj.toString();
};

const loginSchema = z.object({
	email: z.email(),
});

const SSOLogin: FC<PropsWithChildren> = () => {
	const [isFormLoading, setIsFormLoading] = useState(false);
	const { state: emailState, handleEmailChange, handleEmailValidation } = useEmailInput();
	const { setLoginMethod, setNotification } = useZustand();
	const theme = useTheme();

	const isFormValid = loginSchema.safeParse({ email: emailState.value }).success;
	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setIsFormLoading(true);
		try {
			const samlQuery = getSamlQuery(emailState.value);
			const saml = await queryClient.ensureQueryData({
				queryKey: samlQuery.queryKey,
				queryFn: samlQuery.queryFn,
			});

			if (saml.data && saml.data.samlEnabled) {
				globalThis.location.href = getWithQueryParam(saml.data.loginUrl, 'relayState', 'tradeshift-go');
			} else {
				setNotification([
					{
						type: notificationStrings.WARNING,
						message: 'You entered an incorrect email address or SSO is not enabled.',
					},
				]);
			}
		} catch {
			setNotification([
				{
					type: notificationStrings.WARNING,
					message: 'An error occurred, please try again later.',
				},
			]);
		} finally {
			setIsFormLoading(false);
		}
	};

	return (
		<form
			data-testid="form"
			style={{
				padding: `0 ${theme.tsGoUnit.tsUnit} ${theme.tsGoUnit.tsUnit}`,
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				gap: theme.tsGoUnit.tsUnit,
			}}
			onSubmit={handleSubmit}
		>
			<FormControl style={{ padding: 0 }} fullWidth variant="outlined" error={emailState.isError}>
				<TextField
					data-testid="email"
					label={'Email'}
					style={{ padding: 0, margin: 0 }}
					type="email"
					required
					fullWidth
					margin="normal"
					variant="outlined"
					autoComplete="off"
					value={emailState.value}
					error={emailState.isError}
					onChange={e => handleEmailChange(e.target.value)}
					onBlur={handleEmailValidation}
					slotProps={{
						htmlInput: {
							'data-testid': 'email',
						},

						inputLabel: {
							style: { fontWeight: theme.typography.tsFontWeight.default },
						},
					}}
				/>
				{emailState.isError && (
					<FormHelperText style={{ marginLeft: 0 }}>
						<ErrorIcon fontSize="small" style={{ verticalAlign: 'middle' }} /> {'Invalid email address'}
					</FormHelperText>
				)}
			</FormControl>
			<Button
				data-testid="login-button"
				style={{
					width: '100%',
					fontSize: theme.typography.pxToRem(theme.typography.tsFontSize.default),
				}}
				color={UI_BUTTON_PRIMARY}
				disabled={!isFormValid}
				type="submit"
				loading={isFormLoading}
				loadingPosition="start"
				startIcon={<></>}
				variant="contained"
			>
				{'Use single signon to sign in'}
			</Button>
			<StyledBox>
				<StyledText variant="body1">{'Or use your credentials'}</StyledText>
			</StyledBox>
			<SimpleButton
				dataTestId="backToLogin-btn"
				style={{
					width: '100%',
					border: `1px solid ${theme.palette.tsGoColor.pink}`,
					color: theme.palette.tsGoColor.pink,
					fontSize: theme.typography.pxToRem(theme.typography.tsFontSize.default),
				}}
				color={UI_BUTTON_SECONDARY}
				label={'Back to login'}
				onClick={() => setLoginMethod({ isPasswordLogin: true })}
			/>
		</form>
	);
};

export default SSOLogin;
